import React, { useEffect, useState } from 'react';
import { useAudioPlayer } from 'apm-react-audio-player';
// import NowPlayingClient from 'nowplaying-client-2'; // Lazy loaded when needed
import PropTypes from 'prop-types';

export const defaultAudioState = {
  audioPlayerRef: undefined,
  progressBarRef: undefined,
  audioSource: [
    'https://hls.stream.publicradio.org/nis-hls/playlist.m3u8',
    'https://nis.stream.publicradio.org/nis.aac',
    'https://nis.stream.publicradio.org/nis.mp3',
  ],
  audioTitle: 'MPR News',
  audioDescription: '',
  nowPlayingTitle: 'MPR News',
  audioThumbnail: null,
  audioSubtitle: 'Live Stream',
  isAudioPlaying: false,
  isPlayerVisible: false,
  isAudioLive: true,
  nowPlayingThumbnail: null,
  playerInstance: null,
  playlist: {},
  audioThumbnailSrc: '/img/mpr-news-logo-thumbnail.svg',
  audioThumbnailAlt: 'MPR News logo',
  audioThumbnailPad: false,
  // handleAudioButtonClick: (audioSource, audioTitle, audioSubtitle) => undefined,
  // loadPlayer: () => undefined,
  setAudio: (
    audioSource,
    audioTitle,
    audioSubtitle,
    audioThumbnailSrc,
    audioThumbnailAlt
  ) => undefined,
  switchAudio: (
    audioSource,
    audioTitle,
    audioSubtitle,
    audioThumbnailSrc,
    audioThumbnailAlt
  ) => undefined,
  resetLivePlayer: () => undefined,
  togglePlaying: () => undefined,
  play: () => undefined,
  pause: () => undefined,
  toggleMute: () => undefined,
  volumeControl: () => undefined,
  rewindControl: () => undefined,
  forwardControl: () => undefined,
};

const AudioPlayerContext = React.createContext(defaultAudioState);

export const AudioPlayerProvider = ({ children }) => {
  const [state, setState] = useState({
    ...defaultAudioState,
  });
  const audioPlayerRef = React.useRef();
  const progressBarRef = React.useRef();
  const hlsRef = React.useRef(null);
  const pendingPlayRef = React.useRef(false);
  const showImageCache = React.useRef(new Map());
  const currentShowIdRef = React.useRef(null);
  const isSafari = React.useRef(
    typeof navigator !== 'undefined' &&
      /^((?!chrome|android).)*safari/i.test(navigator.userAgent)
  );

  const {
    onLoadedMetadata,
    calculateTime,
    volumeControl,
    toggleMute,
    isMuted,
    changePlayerCurrentTime,
    play,
    pause,
    isPlaying,
    isFinishedPlaying,
    currentTime,
    duration,
    formatCalculateTime,
    rewindControl,
    forwardControl,
    resetDuration,
    audioThumbnailSrc = state.audioThumbnailSrc,
    audioThumbnailAlt = state.audioThumbnailAlt,
  } = useAudioPlayer(audioPlayerRef, progressBarRef, { hlsRef });

  useEffect(() => {
    if (isFinishedPlaying) {
      setAudio(
        defaultAudioState.audioSource,
        defaultAudioState.audioTitle,
        defaultAudioState.audioSubtitle
      );
    }
  }, [isFinishedPlaying]);

  useEffect(() => {
    if (pendingPlayRef.current) {
      pendingPlayRef.current = false;
      play();
    }
  }, [state.audioSource]);

  useEffect(() => {
    let scheduleRegistration;

    if (state.isAudioLive) {
      // Dynamically import NowPlayingClient only when audio is live
      import('nowplaying-client-2').then((module) => {
        const NowPlayingClient = module.default;
        const client = new NowPlayingClient({
          server: 'wss://nowplayingv2.publicradio.org',
        });

        scheduleRegistration = client.register_callback(
          'mpr-news',
          'schedule',
          (data) => {
            if (state.isAudioLive && data?.schedule) {
              setNowPlayingTitle(data.schedule);
            }
          }
        );
      });
    }

    return () => {
      if (scheduleRegistration) {
        scheduleRegistration.unregister();
      }
    };
  }, [state.isAudioLive]);

  // The player renders a square thumbnail. Prefer the square crop, but some
  // scheduler images (e.g. the BBC World Service logo) only have thumbnail +
  // uncropped crops rendered — fall through so we show any available image
  // rather than the default placeholder.
  const imageSrc = (image) => {
    const ar = image?.aspect_ratios;
    return (
      ar?.square?.instances?.[0]?.url ||
      ar?.uncropped?.instances?.[0]?.url ||
      ar?.thumbnail?.instances?.[0]?.url ||
      image?.fallback
    );
  };

  const updateNowPlaying = (nowPlayingTitle, image) => {
    setState((prevState) => {
      return {
        ...prevState,
        nowPlayingTitle,
        audioTitle: nowPlayingTitle,
        audioThumbnailSrc:
          imageSrc(image) || defaultAudioState.audioThumbnailSrc,
        audioThumbnailAlt:
          image?.alt_text || defaultAudioState.audioThumbnailAlt,
        // PNGs are typically transparent logos (e.g. BBC World Service) whose
        // dark text vanishes on the player's dark background — pad them on white.
        audioThumbnailPad: image?.type === 'image/png',
      };
    });
  };

  const setNowPlayingTitle = (schedule) => {
    const slot = schedule[0];
    const show = slot.shows[0];
    const hostImage = slot.people[0]?.image || null;
    let nowPlayingTitle = show.name;
    if (slot.people[0]) {
      nowPlayingTitle += ` with ${slot.people[0].name}`;
    }

    const showId = show?.id;
    currentShowIdRef.current = showId;

    // Prefer the show's own image in all cases. It's never inline in the
    // schedule feed (only host images are), so unless the scheduler adds it we
    // must fetch /shows/:id to get it. Fall back to the host headshot only when
    // the show has no image. The proxy is CloudFront-cached, so this fetch on
    // (nearly) every slot resolves from the edge.
    if (show?.image) {
      updateNowPlaying(nowPlayingTitle, show.image);
      return;
    }

    // No show id to look up — use the host image if we have one.
    if (!showId) {
      updateNowPlaying(nowPlayingTitle, hostImage);
      return;
    }

    // Set the title now; wait for the fetch to resolve the image so we never
    // briefly paint the host headshot before the preferred show image.
    updateNowPlaying(nowPlayingTitle, null);

    const applyImage = (showImage) => {
      // Only apply if this show is still the current slot — a slow response
      // must not paint a stale image after the show has changed.
      if (currentShowIdRef.current === showId) {
        updateNowPlaying(nowPlayingTitle, showImage || hostImage);
      }
    };

    const cached = showImageCache.current.get(showId);
    if (cached !== undefined) {
      applyImage(cached);
      return;
    }

    fetch(`/api/schedule/show/${showId}`)
      .then((res) => res.json())
      .then((data) => {
        const showImage = data?.shows?.[0]?.image || null;
        showImageCache.current.set(showId, showImage);
        applyImage(showImage);
      })
      .catch(() => {
        // On failure, fall back to the host image if this slot is still current.
        if (currentShowIdRef.current === showId) {
          updateNowPlaying(nowPlayingTitle, hostImage);
        }
      });
  };

  const setAudio = (
    audioSource,
    audioTitle = defaultAudioState.audioTitle,
    audioSubtitle = defaultAudioState.audioSubtitle,
    audioThumbnailSrc = defaultAudioState.audioThumbnailSrc,
    audioThumbnailAlt = defaultAudioState.audioThumbnailAlt
  ) => {
    setState((prevState) => {
      return {
        ...prevState,
        audioSource,
        audioTitle,
        audioSubtitle,
        isAudioLive:
          JSON.stringify(audioSource) ===
          JSON.stringify(defaultAudioState.audioSource),
        audioThumbnailAlt,
        audioThumbnailSrc,
      };
    });
  };

  const switchAudio = (
    audioSource,
    audioTitle = defaultAudioState.audioTitle,
    audioSubtitle = defaultAudioState.audioSubtitle,
    audioThumbnailSrc = defaultAudioState.audioThumbnailSrc,
    audioThumbnailAlt = defaultAudioState.audioThumbnailAlt
  ) => {
    if (isSafari.current) {
      // Safari requires play() to be called synchronously within the user gesture.
      // Destroy the live-stream HLS instance first — it's always active at page load
      // (ReactAudioPlayerInner runs loadSource() + attachMedia() on mount). Without
      // this, hlsRef.current is truthy when play() runs, routing into the HLS branch
      // and conflicting with the direct src pre-set below.
      // Then pre-set src on the DOM element so the gesture-scoped play() targets the
      // new source. ReactAudioPlayerInner will skip audio.load() when it detects
      // Safari + isPlaying, preventing an AbortError from cancelling the play.
      if (audioPlayerRef.current) {
        if (hlsRef.current) {
          hlsRef.current.destroy();
          hlsRef.current = null;
        }
        audioPlayerRef.current.src = audioSource;
      }
      setAudio(
        audioSource,
        audioTitle,
        audioSubtitle,
        audioThumbnailSrc,
        audioThumbnailAlt
      );
      play();
    } else {
      // Chrome/Firefox: destroy HLS and clear the dead blob: src synchronously before
      // the state update. hls.js revokes the MediaSource object URL on destroy() but
      // does not call removeAttribute('src'), so audio.src still points to the revoked
      // blob URL. If left in place, audio.load() in ReactAudioPlayerInner's effect
      // requests it and gets ERR_FILE_NOT_FOUND. Removing the attribute here first
      // ensures load() picks up the <source> children (the MP3 URL) instead.
      if (audioPlayerRef.current && hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
        audioPlayerRef.current.removeAttribute('src');
      }
      // Set the flag before the state update so the audioSource useEffect (parent)
      // fires play() after ReactAudioPlayerInner's effect (child) has already called
      // audio.load() — guaranteed by React's child-before-parent effect ordering.
      pendingPlayRef.current = true;
      setAudio(
        audioSource,
        audioTitle,
        audioSubtitle,
        audioThumbnailSrc,
        audioThumbnailAlt
      );
    }
  };

  const playLive = () => {
    if (state.isAudioLive && hlsRef.current && audioPlayerRef.current) {
      if (isSafari.current) {
        // detachMedia() destroys the old MediaSource, clearing audio.buffered to length 0.
        // attachMedia() creates a fresh MediaSource bound to the audio element.
        // play() then sees buffered.length === 0 and waits for hlsFragBuffered before
        // calling safePlay(), ensuring fresh content from the live edge and preventing
        // stale pre-discontinuity segments from causing a forward seek into an ad.
        hlsRef.current.detachMedia();
        hlsRef.current.attachMedia(audioPlayerRef.current);
      }
      // Seek to live edge via hls.js so stale buffered segments don't block resume.
      hlsRef.current.startLoad(-1);
    }
    play();
  };

  const pauseAudio = () => {
    if (hlsRef.current) {
      hlsRef.current.stopLoad();
    }
    pause();
  };

  const togglePlayingLive = () => {
    if (!isPlaying) {
      playLive();
    } else {
      pauseAudio();
    }
  };

  return (
    <AudioPlayerContext.Provider
      value={{
        ...state,
        isAudioPlaying: isPlaying,
        setAudio,
        switchAudio,
        onLoadedMetadata: () => {
          onLoadedMetadata();
          if (!state.isAudioLive && isPlaying) {
            play();
          }
        },
        calculateTime,
        formatCalculateTime,
        volumeControl,
        togglePlaying: togglePlayingLive,
        toggleMute,
        rewindControl,
        forwardControl,
        isMuted,
        changePlayerCurrentTime,
        isPlaying,
        isFinishedPlaying,
        currentTime,
        duration,
        audioPlayerRef,
        progressBarRef,
        hlsRef,
        audioThumbnailSrc,
        audioThumbnailAlt,
        play: playLive,
        pause: pauseAudio,
        resetDuration,
      }}
    >
      {children}
    </AudioPlayerContext.Provider>
  );
};

export default AudioPlayerContext;

AudioPlayerProvider.propTypes = {
  children: PropTypes.node,
};
